import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type ThemePref = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "tf-theme";

interface ThemeContextValue {
  /** The user's preference, including "system". */
  theme: ThemePref;
  /** The actual theme on screen right now ("light" | "dark"). */
  resolvedTheme: ResolvedTheme;
  setTheme: (t: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * The theme somebody gets before they have ever said what they want.
 *
 * ⚠️ Written in FOUR places, and they cannot be collapsed into one: the class on <html>
 * in index.html, the pre-paint script in public/theme-init.js, and this constant. Each
 * runs at a different moment — markup, before first paint, and after React mounts — and
 * the pre-paint one cannot import from here because it is a plain external script (an
 * inline module would need 'unsafe-inline' in script-src, which CLAUDE.md forbids).
 *
 * A stored preference always wins over this. Only `setTheme` ever writes storage, so
 * changing this default moves nobody who has already chosen.
 */
const DEFAULT_THEME: ThemePref = "light";

function systemPrefersDark(): boolean {
  // ⚠️ Falls back to LIGHT, not dark, when there is no window to ask. This is the
  // "system" branch with no system to read — during SSR or a non-browser render — and
  // guessing the opposite of the app default means the first paint disagrees with
  // theme-init.js and produces the flash both of them exist to prevent.
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(pref: ThemePref): ResolvedTheme {
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref;
}

/**
 * Reads the stored preference, or the default for anything that is not one of the three.
 *
 * ⚠️ Validated rather than cast. `getItem` returns `string | null`, and asserting that to
 * `ThemePref` let a junk value — a stale key from an older build, a hand-edited entry —
 * travel straight through `resolve` as if it were a resolved theme. `apply` would then
 * toggle BOTH classes off, leaving the page on whatever `:root` happens to be rather than
 * on the default this module is meant to guarantee. Unknown means unset.
 */
function readStored(): ThemePref {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === "light" || raw === "dark" || raw === "system" ? raw : DEFAULT_THEME;
  } catch {
    // Storage unavailable (private mode, blocked cookies) — same fallback as
    // theme-init.js takes for the same reason.
    return DEFAULT_THEME;
  }
}

function apply(resolved: ResolvedTheme, animate: boolean) {
  const root = document.documentElement;
  if (animate) {
    root.classList.add("theme-transition");
    window.setTimeout(() => root.classList.remove("theme-transition"), 300);
  }
  root.classList.toggle("dark", resolved === "dark");
  root.classList.toggle("light", resolved === "light");
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Matches theme-init.js and the class on <html>, so the first client render agrees
  // with what is already painted; the effect below reconciles with localStorage.
  const [theme, setThemeState] = useState<ThemePref>(DEFAULT_THEME);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(resolve(DEFAULT_THEME));

  // Hydrate from storage once mounted, and re-assert the class on <html> so it
  // matches the stored preference even if React reconciled it during hydration.
  useEffect(() => {
    const stored = readStored();
    const r = resolve(stored);
    setThemeState(stored);
    setResolvedTheme(r);
    apply(r, false);
  }, []);

  // Follow OS changes while in "system" mode.
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const next = systemPrefersDark() ? "dark" : "light";
      setResolvedTheme(next);
      apply(next, true);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = useCallback((next: ThemePref) => {
    setThemeState(next);
    localStorage.setItem(STORAGE_KEY, next);
    const r = resolve(next);
    setResolvedTheme(r);
    apply(r, true);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
